import axios, { AxiosInstance } from 'axios';
import { GenerelConfig, Modifier, Product } from '../types';
import { SearchProductsRequest } from '../store/types';
import appConfig from '../config/app.config';
import { UploadProductImageError } from '../errors/upload-product-image.error';
import { storeFrontAuthSelectors } from '../store/store-front-auth';

class ApiClient {
  private readonly instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: appConfig.API_SERVER_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    this.addResponseMiddlewares();
  }

  /**
   * Adding success and error middlewares that execute after an http response is received:
   * success middleware - undefined (no need to do anything)
   * error middleware - adding status code to the error object
   */
  private addResponseMiddlewares(): void {
    this.instance.interceptors.response.use(undefined, (error: any) => {
      if (axios.isAxiosError(error) && error.response) {
        error.code = String(error.response.status);
      } else {
        error.code = '500';
      }

      return Promise.reject(error);
    });
  }

  private getHeaders(): Record<string, string> {
    let bearerType: string = '';
    let authToken: string = '';

    if (localStorage.getItem('okta-token-storage')) {
      bearerType = 'Bearer-okta';
      authToken = JSON.parse(localStorage.getItem('okta-token-storage')!).accessToken.accessToken;
    } else if (storeFrontAuthSelectors.selectAuthToken()) {
      bearerType = 'Bearer-store-front';
      authToken = storeFrontAuthSelectors.selectAuthToken()!;
    }

    return {
      Authorization: `${bearerType} ${authToken}`
    };
  }

  async getProducts(queryParams: SearchProductsRequest): Promise<Product[]> {
    const response = await this.instance.get('', {
      params: queryParams,
      headers: this.getHeaders()
    });
    return response.data;
  }

  async getProductById(id: string): Promise<Product> {
    const response = await this.instance.get(`/${id}`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  async getGeneralConfig(): Promise<GenerelConfig> {
    const response = await this.instance.get('config/general-config', {
      headers: this.getHeaders()
    });
    return response.data;
  }

  async getCategoriesForPricelist(pricelistId: string): Promise<Record<string, string[]>> {
    const response = await this.instance.get(`/categories/${pricelistId}`, {
      headers: this.getHeaders()
    });

    return response.data;
  }

  async uploadProductImageByFile(formData: FormData): Promise<string> {
    try {
      const response = await this.instance.post<string>('/upload/product-image-file', formData, {
        headers: {
          ...this.getHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        throw error;
      }

      const errorData = error.response!.data;
      throw new UploadProductImageError(
        errorData.message,
        errorData.statusCode || error.response?.status,
        errorData.type,
        errorData.args
      );
    }
  }

  async uploadProductImageByUrl(url: string): Promise<string> {
    try {
      const response = await this.instance.post<string>(
        '/upload/product-image-url',
        { url },
        {
          headers: {
            ...this.getHeaders()
          }
        }
      );
      return response.data;
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        throw error;
      }

      const errorData = error.response!.data;
      throw new UploadProductImageError(
        errorData.message,
        errorData.statusCode || error.response?.status,
        errorData.type,
        errorData.args
      );
    }
  }

  async updateProduct(product: Product, isNewProduct?: boolean): Promise<Product> {
    const response = await this.instance.patch(
      `/${product.productId}`,
      {
        product: product,
        pricelistId: product.pricelistId,
        isNewProduct: isNewProduct ?? false
      },
      {
        headers: this.getHeaders()
      }
    );
    return response.data;
  }

  async getPricelistModifiers(pricelistId: string): Promise<Modifier[]> {
    const response = await this.instance.get(`/modifiers/${pricelistId}`, {
      headers: this.getHeaders()
    });
    return response.data;
  }
}

export default new ApiClient();
